import { useEffect, useRef, useCallback } from 'react';
import { useThemeMode } from '../contexts/ThemeContext';

/*
 * AlphanetixMesh — Signature brand background
 * 
 * A full-viewport canvas that renders a living network topology:
 *   • Scattered nodes form an organic mesh
 *   • Thin connection lines link nearby nodes
 *   • Mouse proximity brightens nearby nodes/edges
 *   • Very subtle – sits behind glassmorphic content as brand texture
 */

// ─── Config ───────────────────────────────────────────────────
const NODE_COUNT = 40;
const CONNECTION_DISTANCE = 150;    // max px between nodes to draw a line
const MOUSE_RADIUS = 220;           // px radius of mouse influence
const NODE_DRIFT_SPEED = 0.15;      // px per frame max drift

export default function AlphanetixMesh() {
  const canvasRef = useRef(null);
  const { isLight, isHydrated } = useThemeMode();
  const animRef = useRef(null);
  const nodesRef = useRef([]);
  const mouseRef = useRef({ x: -9999, y: -9999 });
  const dimRef = useRef({ w: 0, h: 0 });
  const themeRef = useRef(isLight);
  const hiddenRef = useRef(false);
  const lastFrameRef = useRef(0);

  // Keep themeRef in sync without re-initializing nodes
  useEffect(() => { themeRef.current = isLight; }, [isLight]);

  // ─── Palette ──────────────────────────────────────────────
  // ─── Palette (pre-computed, no per-frame allocation) ─────
  const PALETTE_LIGHT = {
    nodeFill:   'rgba(99, 102, 241, 0.10)',
    nodeGlow:   'rgba(99, 102, 241, 0.30)',
    line:       'rgba(79, 70, 229, 0.04)',
    lineActive: 'rgba(99, 102, 241, 0.12)',
  };
  const PALETTE_DARK = {
    nodeFill:   'rgba(16, 185, 129, 0.25)',
    nodeGlow:   'rgba(16, 185, 129, 0.5)',
    line:       'rgba(16, 185, 129, 0.08)',
    lineActive: 'rgba(16, 185, 129, 0.22)',
  };

  // ─── Init nodes ───────────────────────────────────────────
  const initNodes = useCallback((w, h) => {
    const nodes = [];
    for (let i = 0; i < NODE_COUNT; i++) {
      nodes.push({
        x: Math.random() * w,
        y: Math.random() * h,
        r: 1.2 + Math.random() * 1.5,          // base radius
        vx: (Math.random() - 0.5) * NODE_DRIFT_SPEED,
        vy: (Math.random() - 0.5) * NODE_DRIFT_SPEED,
        phase: Math.random() * Math.PI * 2,     // for subtle size pulsing
      });
    }
    nodesRef.current = nodes;
  }, []);

  // ─── Resize handler ──────────────────────────────────────
  const handleResize = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = window.innerWidth;
    const h = window.innerHeight;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    dimRef.current = { w, h };
    // Re-scatter nodes on resize
    initNodes(w, h);
  }, [initNodes]);

  // ─── Mouse tracking ──────────────────────────────────────
  const handleMouseMove = useCallback((e) => {
    mouseRef.current = { x: e.clientX, y: e.clientY };
  }, []);

  const handleMouseLeave = useCallback(() => {
    mouseRef.current = { x: -9999, y: -9999 };
  }, []);

  // ─── Animation loop ──────────────────────────────────────
  const animate = useCallback(() => {
    if (hiddenRef.current) { animRef.current = null; return; }
    const now = performance.now();
    // Throttle to ~30fps
    if (now - lastFrameRef.current < 33) {
      animRef.current = requestAnimationFrame(animate);
      return;
    }
    lastFrameRef.current = now;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const { w, h } = dimRef.current;
    const nodes = nodesRef.current;
    const mouse = mouseRef.current;
    const colors = themeRef.current ? PALETTE_LIGHT : PALETTE_DARK;
    const time = performance.now() * 0.001;

    ctx.clearRect(0, 0, w, h);

    // ── Update node positions (gentle drift) ──
    for (const node of nodes) {
      node.x += node.vx;
      node.y += node.vy;
      // Wrap around edges with padding
      if (node.x < -20) node.x = w + 20;
      if (node.x > w + 20) node.x = -20;
      if (node.y < -20) node.y = h + 20;
      if (node.y > h + 20) node.y = -20;
    }

    // ── Draw connections (no intermediate array allocation) ──
    for (let i = 0; i < nodes.length; i++) {
      for (let j = i + 1; j < nodes.length; j++) {
        const dx = nodes[j].x - nodes[i].x;
        const dy = nodes[j].y - nodes[i].y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < CONNECTION_DISTANCE) {
          // Mouse proximity — brighten edges near cursor
          const midX = (nodes[i].x + nodes[j].x) * 0.5;
          const midY = (nodes[i].y + nodes[j].y) * 0.5;
          const mdx = midX - mouse.x;
          const mdy = midY - mouse.y;
          const mDist = Math.sqrt(mdx * mdx + mdy * mdy);
          const mouseFactor = mDist < MOUSE_RADIUS ? 1 - mDist / MOUSE_RADIUS : 0;
          const opacity = 1 - dist / CONNECTION_DISTANCE;

          ctx.beginPath();
          ctx.moveTo(nodes[i].x, nodes[i].y);
          ctx.lineTo(nodes[j].x, nodes[j].y);
          ctx.strokeStyle = mouseFactor > 0
            ? colors.lineActive
            : colors.line;
          ctx.lineWidth = 0.5 + mouseFactor * 0.8;
          ctx.globalAlpha = opacity * (0.5 + mouseFactor * 0.5);
          ctx.stroke();
          ctx.globalAlpha = 1;
        }
      }
    }

    // ── Draw nodes ──
    for (const node of nodes) {
      const ndx = node.x - mouse.x;
      const ndy = node.y - mouse.y;
      const mDist = Math.sqrt(ndx * ndx + ndy * ndy);
      const mouseFactor = mDist < MOUSE_RADIUS ? 1 - mDist / MOUSE_RADIUS : 0;
      const pulse = 1 + Math.sin(time * 1.5 + node.phase) * 0.15;
      const r = node.r * pulse * (1 + mouseFactor * 0.6);

      // Glow ring on mouse proximity
      if (mouseFactor > 0.1) {
        ctx.beginPath();
        ctx.arc(node.x, node.y, r + 4, 0, Math.PI * 2);
        ctx.fillStyle = colors.nodeGlow;
        ctx.globalAlpha = mouseFactor * 0.25;
        ctx.fill();
        ctx.globalAlpha = 1;
      }

      ctx.beginPath();
      ctx.arc(node.x, node.y, r, 0, Math.PI * 2);
      ctx.fillStyle = mouseFactor > 0.1 ? colors.nodeGlow : colors.nodeFill;
      ctx.fill();
    }

    animRef.current = requestAnimationFrame(animate);
  }, []);

  // ─── Setup & teardown ─────────────────────────────────────
  useEffect(() => {
    if (!isHydrated) return;

    handleResize();
    window.addEventListener('resize', handleResize);
    window.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseleave', handleMouseLeave);

    animRef.current = requestAnimationFrame(animate);

    // Pause when tab is hidden (Page Visibility API)
    const onVisChange = () => {
      hiddenRef.current = document.hidden;
      if (!document.hidden && !animRef.current) {
        lastFrameRef.current = performance.now();
        animRef.current = requestAnimationFrame(animate);
      }
    };
    document.addEventListener('visibilitychange', onVisChange);

    return () => {
      document.removeEventListener('visibilitychange', onVisChange);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseleave', handleMouseLeave);
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, [isHydrated, handleResize, handleMouseMove, handleMouseLeave, animate]);

  // Respect reduced motion
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handler = () => {
      if (mq.matches && animRef.current) {
        cancelAnimationFrame(animRef.current);
      } else if (!mq.matches) {
        animRef.current = requestAnimationFrame(animate);
      }
    };
    mq.addEventListener('change', handler);
    if (mq.matches && animRef.current) {
      cancelAnimationFrame(animRef.current);
    }
    return () => mq.removeEventListener('change', handler);
  }, [animate]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 0,
        pointerEvents: 'none',
      }}
    />
  );
}
